import React, { useEffect, useMemo } from "react";
import Paragraph from "atoms/typography/paragraph";
import DeprecatedShow from "atoms/wrappers/show";
import { ChecklistItemNoteRecord } from "models/view-models/checklist-item-notes/checklist-item-note-record";
import RichTextArea from "molecules/rich-text-area/rich-text-area";
import { t } from "utilities/localization-utils";
import { CodeReferencePill } from "organisms/code-reference-pill/code-reference-pill";
import SectionRecord from "models/view-models/section-record";
import { ClosableCodeReferencePreview } from "organisms/code-reference-block-editor/closable-code-reference-preview";
import { useSectionPreview } from "organisms/checklist-item-notes/hooks/use-section-preview";
import useQuillImageTagSanitizer from "utilities/hooks/quill/use-quill-image-tag-sanitizer";
import { useSharedPageContext } from "utilities/contexts/shared-page/shared-page-context";

interface ChecklistItemNoteDisplayProps {
    note: ChecklistItemNoteRecord;
    printable: boolean;
}

const CSS_CLASS_NAME = "c-checklist-item-note-display";

export const ChecklistItemNoteDisplay: React.FC<
    ChecklistItemNoteDisplayProps
> = ({ note, printable }) => {
    const { restoreImageRichTextContent } = useQuillImageTagSanitizer();
    const [restoredNoteText, setRestoredNoteText] = React.useState("");
    const { pageGuid, recipientGuid } = useSharedPageContext();

    useEffect(() => {
        if (note.text == null || note.text === "") return;
        restoreImageRichTextContent(note.text, pageGuid, recipientGuid).then(
            (restored) => {
                setRestoredNoteText(restored);
            }
        );
    }, [
        note.text,
        restoreImageRichTextContent,
        restoredNoteText,
        pageGuid,
        recipientGuid,
    ]);

    const availableSections = useMemo(
        () =>
            note.checklistItemNoteSections
                .map((cins) => cins.section)
                .filter((s) => s != null),
        [note.checklistItemNoteSections]
    );
    const { previewingSections, toggleSectionPreview, removeSectionPreview } =
        useSectionPreview(availableSections, printable);

    const isFocused = (section: SectionRecord): boolean => {
        return previewingSections.map((s) => s.id).includes(section.id);
    };

    const handleCodeReferencePillClick = (section: SectionRecord): void => {
        toggleSectionPreview(section);
    };

    const handleClosePreviewClick = (section: SectionRecord): void => {
        removeSectionPreview(section);
    };

    return (
        <DeprecatedShow when={note.hasContent()}>
            <div className={CSS_CLASS_NAME}>
                <DeprecatedShow when={note.hasText()}>
                    <div className={`${CSS_CLASS_NAME}__content-section`}>
                        <Paragraph cssClassName={`${CSS_CLASS_NAME}__header`}>
                            {t("note").toLocaleUpperCase()}
                        </Paragraph>
                        <RichTextArea
                            content={restoredNoteText}
                            cssClassName={`${CSS_CLASS_NAME}__text`}
                        />
                    </div>
                </DeprecatedShow>
                <DeprecatedShow when={note.hasCodeReferences()}>
                    <div className={`${CSS_CLASS_NAME}__content-section`}>
                        <Paragraph cssClassName={`${CSS_CLASS_NAME}__header`}>
                            {t("codeReference").toLocaleUpperCase()}
                        </Paragraph>
                        <div
                            className={`${CSS_CLASS_NAME}__code-reference-pills`}>
                            {note.checklistItemNoteSections.map((cins) => (
                                <CodeReferencePill
                                    cssClassName={`${CSS_CLASS_NAME}__code-reference-pill`}
                                    focused={isFocused(cins.section!)}
                                    key={cins.id}
                                    section={cins.section!}
                                    publication={cins.section!.publication}
                                    onReferenceClick={
                                        handleCodeReferencePillClick
                                    }
                                />
                            ))}
                        </div>
                        {previewingSections.map((s) => (
                            <ClosableCodeReferencePreview
                                onClose={() => handleClosePreviewClick(s)}
                                referenceSection={s}
                            />
                        ))}
                    </div>
                </DeprecatedShow>
            </div>
        </DeprecatedShow>
    );
};
